// Type definitions for Admin Dashboard

export type UserRole = 'user' | 'admin';
export type AccountStatus = 'active' | 'inactive' | 'suspended';

export type AttackType =
  | 'Voice Conversion'
  | 'Text-to-Speech'
  | 'Voice Cloning'
  | 'Synthetic Speech'
  | 'Replay Attack'
  | 'Benign';

export interface UserProfile {
  uid: string;
  email: string;
  displayName?: string;
  phoneNumber?: string;
  role: UserRole;
  accountStatus: AccountStatus;
  isGuestMode: boolean;
  termsAccepted: boolean;
  createdAt: Date;
  lastLoginAt?: Date;
}

export interface CallRecord {
  id: string;
  userId: string;
  timestamp: Date;
  duration: number;
  deepfakeLikelihood: number;
  isDeepfake: boolean;
  modelUsed: string;
  attackType: AttackType;
  deviceId?: string;
}

export interface DetectionResult {
  timestamp: number;
  confidenceScore: number;
  isDeepfake: boolean;
  modelUsed: string;
}

export interface SystemAnalytics {
  totalUsers: number;
  activeUsers: number;
  totalCalls: number;
  deepfakeCalls: number;
  detectionRate: number;
  trends: {
    date: string;
    calls: number;
    deepfakes: number;
  }[];
}

export type AuditAction =
  | 'password_reset'
  | 'delete_user'
  | 'update_user'
  | 'update_account_status'
  | 'update_settings'
  | 'view_user'
  | 'export_data';

export interface AuditLog {
  id: string;
  adminId: string;
  adminEmail: string;
  action: AuditAction;
  targetUserId?: string;
  targetUserEmail?: string;
  details: Record<string, any>;
  timestamp: Date;
  ipAddress?: string;
}

export interface PeriodAnalytics {
  userId: string;
  period: 'daily' | 'weekly' | 'monthly';
  date: Date;
  totalCalls: number;
  deepfakeCalls: number;
  avgConfidence: number;
  detectionRate: number;
}

export interface ModelInfo {
  id: string;
  modelName: string;
  version: string;
  modelType: 'fast' | 'accurate' | 'balanced';
  accuracy: number;
  inferenceTimeMs: number;
  memoryUsageMB: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// New interfaces for enhanced analytics
export interface AnalyticsFilters {
  model: string | 'all';
  dateRange: { start: Date; end: Date };
  quickSelect?: 'today' | 'last7days' | 'thisMonth';
}

export interface FilteredAnalytics {
  totalCalls: number;
  legitimateCalls: number;
  deepfakeCalls: number;
  avgConfidenceScore: number;
  avgAccuracy: number;
  attackTypeDistribution: { type: AttackType; count: number }[];
  monitoringTrends: { date: string; calls: number; deepfakes: number }[];
}

// Toast notification types
export interface Toast {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  duration?: number;
}

// ============================================================================
// SESSION & DEVICE TRUST TYPES
// ============================================================================

// Device info for identification
export interface DeviceInfo {
  deviceId: string;
  deviceName: string;
  deviceType: 'mobile' | 'web';
  platform: string;
}

// Local storage session state (localStorage)
export interface DeviceSessionState {
  deviceId: string;
  deviceName: string;
  userId: string;
  smsVerifiedAt: number; // Unix timestamp in milliseconds
  lastActivityAt: number; // Unix timestamp in milliseconds
  sessionVersion: number; // For schema migrations
}

// Firestore trusted device record
export interface TrustedDevice {
  deviceId: string;
  deviceName: string;
  deviceType: 'mobile' | 'web';
  platform: string;
  firstTrustedAt: any; // Firestore Timestamp
  lastSmsVerifiedAt: any; // Firestore Timestamp
  lastActivityAt: any; // Firestore Timestamp
  isActive: boolean;
  ipAddress?: string;
  userAgent?: string;
}

// Session validation result
export interface SessionValidationResult {
  isValid: boolean;
  needsSms2fa: boolean;
  reason?:
    | 'new_device'
    | 'inactivity_timeout'
    | 'device_untrusted'
    | 'session_expired'
    | 'logged_out'
    | 'no_session';
}

// Session constants
export const SESSION_CONSTANTS = {
  INACTIVITY_TIMEOUT_MS: 14 * 24 * 60 * 60 * 1000, // 14 days
  MAX_TRUSTED_DEVICES: 5,
  SESSION_VERSION: 1,
  STORAGE_KEYS: {
    DEVICE_ID: 'unveil_device_id',
    SESSION_STATE: 'unveil_session_state',
    LAST_ACTIVITY: 'unveil_last_activity',
  },
} as const;
